/**
 * /operations/leaves/{leave_id} — Rich Leave Detail Page (M3X.LEAVES PR11, §4.7).
 *
 * Mirrors loans LoanDetailPageRichPage pattern. 4 sections:
 *   - Header card with state pill + key dates + days
 *   - 3 AI insight cards (Tashghil/Mutaba'aa/Rawatib)
 *   - Pay-in-advance + custody handover surfaces
 *   - Timeline placeholder
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ErrorPanel, Loading } from "../../../components/Loading";
import { AgentBadge } from "../../../components/ui/AgentBadge";
import { PageHeader } from "../../../components/ui/PageHeader";
import { api } from "../../../lib/api";

function statePill(state: string): { label: string; cls: string } {
  switch (state) {
    case "approved":
    case "in_progress":
      return { label: "🟢 Active", cls: "bg-emerald-100 text-emerald-800" };
    case "submitted":
    case "pending_approval":
      return { label: "🟡 Pending", cls: "bg-amber-100 text-amber-800" };
    case "completed":
      return { label: "⚪ Completed", cls: "bg-slate-100 text-slate-700" };
    case "cancelled":
      return { label: "⚫ Cancelled", cls: "bg-slate-200 text-slate-700" };
    case "denied":
      return { label: "🔴 Denied", cls: "bg-rose-100 text-rose-800" };
    default:
      return { label: state, cls: "bg-slate-100 text-slate-700" };
  }
}

export function LeaveDetailPageRich() {
  const { t } = useTranslation();
  const { leave_id } = useParams<{ leave_id: string }>();

  const q = useQuery({
    queryKey: ["leave-detail-rich", leave_id],
    queryFn: () => api.getLeaveRequest(leave_id ?? ""),
    enabled: Boolean(leave_id),
  });

  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function withBusy(name: string, fn: () => Promise<void>) {
    setBusy(name);
    setErr(null);
    setMsg(null);
    try {
      await fn();
      q.refetch();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleExtend(id: string) {
    const newEnd = prompt(
      t("leave_detail.action_extend_prompt", "New end date (YYYY-MM-DD):") as string,
    );
    if (!newEnd) return;
    await withBusy("extend", async () => {
      await api.extendLeaveRequest(id, { new_end_date: newEnd });
      setMsg(t("leave_detail.action_extend_success", "Leave extended.") as string);
    });
  }
  async function handleReschedule(id: string) {
    const start = prompt(t("leave_detail.action_reschedule_start", "New start (YYYY-MM-DD):") as string);
    const end = prompt(t("leave_detail.action_reschedule_end", "New end (YYYY-MM-DD):") as string);
    if (!start || !end) return;
    await withBusy("reschedule", async () => {
      await api.rescheduleLeaveRequest(id, { new_start_date: start, new_end_date: end });
      setMsg(t("leave_detail.action_reschedule_success", "Leave rescheduled.") as string);
    });
  }
  async function handleEarlyReturn(id: string) {
    const actual = prompt(t("leave_detail.action_early_return_prompt", "Actual return date (YYYY-MM-DD):") as string);
    if (!actual) return;
    await withBusy("early_return", async () => {
      await api.earlyReturnLeaveRequest(id, { actual_end_date: actual });
      setMsg(t("leave_detail.action_early_return_success", "Early return recorded.") as string);
    });
  }
  async function handleTransferType(id: string) {
    const code = prompt(
      t("leave_detail.action_transfer_prompt", "New leave type code (e.g. annual, sick, unpaid):") as string,
    );
    if (!code) return;
    await withBusy("transfer", async () => {
      await api.transferLeaveType(id, { new_leave_type_code: code });
      setMsg(t("leave_detail.action_transfer_success", "Leave type transferred.") as string);
    });
  }
  async function handleCancel(id: string) {
    const reason = prompt(
      t("leave_detail.action_cancel_prompt", "Cancellation reason:") as string,
    );
    if (reason === null) return;
    await withBusy("cancel", async () => {
      await api.cancelLeaveRequest(id, { reason: reason || undefined });
      setMsg(t("leave_detail.action_cancel_success", "Leave cancelled.") as string);
    });
  }
  async function handlePayInAdvance(id: string) {
    await withBusy("advance", async () => {
      const res = await api.issueLeavePayInAdvance(id, {});
      setMsg(
        (t("leave_detail.action_advance_success", "Pay-in-advance issued: {{amt}}") as string).replace(
          "{{amt}}",
          res.advance_amount,
        ),
      );
    });
  }

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) {
    return (
      <ErrorPanel
        message={t("leave_detail.error", "Couldn't load leave detail.")}
      />
    );
  }
  const leave = q.data;
  const pill = statePill(leave.workflow_state);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("leave_detail.title", "Leave Detail")}
        subtitle={`L-${leave.id.slice(0, 8)}`}
      />

      <div className="rounded-lg border border-slate-200 bg-surface-default p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-text-tertiary uppercase mb-1">
              {leave.leave_type_id?.slice(0, 8) ?? "—"}
            </div>
            <h2 className="text-2xl font-bold text-text-primary">
              {leave.start_date} → {leave.end_date}
            </h2>
          </div>
          <span
            className={`text-sm px-3 py-1 rounded-full font-medium ${pill.cls}`}
          >
            {pill.label}
          </span>
        </div>
        <dl className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("leave_detail.days", "Days requested")}
            </dt>
            <dd className="font-mono font-semibold">
              {leave.days_requested}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("leave_detail.submitted", "Submitted")}
            </dt>
            <dd>{leave.submitted_at?.slice(0, 10) ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary text-xs">
              {t("leave_detail.approved", "Approved")}
            </dt>
            <dd>{leave.approved_at?.slice(0, 10) ?? "—"}</dd>
          </div>
        </dl>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InsightCard
          agent="tashghil"
          title={t("leave_detail.tashghil_insight", "Tashghil")}
          body={t(
            "leave_detail.tashghil_body",
            "On track. Approval chain resolved fresh.",
          )}
        />
        <InsightCard
          agent="mutabaa"
          title={t("leave_detail.mutabaa_insight", "Mutaba'aa")}
          body={t(
            "leave_detail.mutabaa_body",
            "Article 109 entitlement OK. No statutory floor breach.",
          )}
        />
        <InsightCard
          agent="rawatib"
          title={t("leave_detail.rawatib_insight", "Rawatib")}
          body={t(
            "leave_detail.rawatib_body",
            "Standard monthly path; no pay-in-advance issued.",
          )}
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <h3 className="font-semibold mb-3">
          {t("leave_detail.actions", "Actions")}
        </h3>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => handleExtend(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-brand-blue text-sm text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
          >
            {busy === "extend" ? t("leave_detail.action_extend_loading", "Extending…") : t("leave_detail.action_extend", "Extend")}
          </button>
          <button
            type="button"
            onClick={() => handleReschedule(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-brand-blue text-sm text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
          >
            {busy === "reschedule" ? t("leave_detail.action_reschedule_loading", "Rescheduling…") : t("leave_detail.action_reschedule", "Reschedule")}
          </button>
          <button
            type="button"
            onClick={() => handleEarlyReturn(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-emerald-400 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          >
            {busy === "early_return" ? t("leave_detail.action_early_return_loading", "Recording…") : t("leave_detail.action_early_return", "Early Return")}
          </button>
          <button
            type="button"
            onClick={() => handleTransferType(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-brand-blue text-sm text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50"
          >
            {busy === "transfer" ? t("leave_detail.action_transfer_loading", "Transferring…") : t("leave_detail.action_transfer", "Transfer Type")}
          </button>
          <button
            type="button"
            onClick={() => handlePayInAdvance(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-amber-400 text-sm text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          >
            {busy === "advance" ? t("leave_detail.action_advance_loading", "Issuing…") : t("leave_detail.action_advance", "Pay in Advance")}
          </button>
          <button
            type="button"
            onClick={() => handleCancel(leave.id)}
            disabled={busy !== null}
            className="px-3 py-1.5 rounded border border-rose-300 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            {busy === "cancel" ? t("leave_detail.action_cancel_loading", "Cancelling…") : t("leave_detail.action_cancel", "Cancel")}
          </button>
        </div>
        {msg && (
          <p className="mt-3 rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
            {msg}
          </p>
        )}
        {err && (
          <p className="mt-3 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {err}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-4">
        <h3 className="font-semibold mb-3">
          {t("leave_detail.timeline", "Timeline")}
        </h3>
        <p className="text-sm text-text-tertiary">
          {t(
            "leave_detail.timeline_body",
            "Every Khidmat / Tashghil / Mutaba'aa / Rawatib event on this leave, with agent attribution + timestamp.",
          )}
        </p>
      </div>
    </div>
  );
}

function InsightCard({
  agent,
  title,
  body,
}: {
  agent: "tashghil" | "mutabaa" | "rawatib" | "khidmat";
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-ai-accent/30 bg-ai-bg p-4">
      <div className="flex items-center gap-2 mb-2">
        <AgentBadge agent={agent} />
        <h4 className="font-semibold text-sm">{title}</h4>
      </div>
      <p className="text-sm text-text-secondary">{body}</p>
    </div>
  );
}
